import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

import { NandaSaMark } from "@/components/BrandMarks";

// Header minimaliste 2026, une marque, quatre univers.
// Énergie · Cosmétiques · Produits & Terroirs · Académie · À propos + CTA Contact.
// Les contenus secondaires (Opportunités, Secteurs, Carnet, Trésors, Devis…)
// restent accessibles via le Footer (architecture secondaire).

const NAV = [
  { to: "/marques/energia-pro", testId: "nav-link-energie", label: { fr: "Énergie", en: "Energy", pt: "Energia" } },
  { to: "/cosmetiques", testId: "nav-link-cosmetiques", label: { fr: "Cosmétiques", en: "Cosmetics", pt: "Cosméticos" } },
  { to: "/terroirs", testId: "nav-link-terroirs", label: { fr: "Produits & Terroirs", en: "Products & Terroirs", pt: "Produtos & Terroirs" } },
  { to: "/marques/academie", testId: "nav-link-academie", label: { fr: "Académie", en: "Academy", pt: "Academia" } },
  { to: "/maison", testId: "nav-link-about", label: { fr: "À propos", en: "About", pt: "Sobre" } },
];

const LanguageSwitcher = ({ vertical = false }) => {
  const { locale, setLocale, locales } = useLanguage();
  return (
    <div className={`flex ${vertical ? "flex-row gap-4" : "items-center gap-1.5"} text-[11px] font-medium tracking-[0.18em]`} data-testid="language-switcher">
      {locales.map((l, i) => (
        <React.Fragment key={l}>
          <button
            type="button"
            onClick={() => setLocale(l)}
            data-testid={`lang-${l}`}
            className={`transition-colors duration-300 ${locale === l ? "text-nanda-ink" : "text-nanda-muted hover:text-nanda-inkSoft"}`}
          >
            {l.toUpperCase()}
          </button>
          {!vertical && i < locales.length - 1 && <span className="text-nanda-muted/50">·</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { locale } = useLanguage();
  const t = (obj) => obj[locale] || obj.fr;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-white/80 backdrop-blur-xl border-b border-black/5 shadow-[0_1px_3px_rgba(0,0,0,0.02)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-12 h-16 md:h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group shrink-0">
          <NandaSaMark size={26} className="text-nanda-ink" />
          <span className="font-sans text-sm md:text-base font-semibold tracking-[0.28em] uppercase text-nanda-ink">NANDA SÁ</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `link-underline text-sm font-normal tracking-wide transition-colors duration-300 hover:text-nanda-ink ${
                  isActive ? "text-nanda-ink" : "text-nanda-inkSoft"
                }`
              }
            >
              {t(item.label)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6 shrink-0">
          <LanguageSwitcher />
          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="inline-flex items-center gap-2 bg-nanda-terracotta text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-[#8F5B3F] transition-colors duration-300"
          >
            {t({ fr: "Contact", en: "Contact", pt: "Contacto" })}
          </Link>
        </div>

        <button
          data-testid="menu-toggle"
          aria-label={open ? "Fermer" : "Menu"}
          className="lg:hidden text-nanda-ink"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-b border-black/5 max-h-[85vh] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-10 flex flex-col gap-7">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${item.testId}`}
                  className="text-2xl font-light tracking-tight text-nanda-ink flex items-center justify-between"
                >
                  {t(item.label)}
                  <ArrowUpRight size={18} strokeWidth={1.5} className="text-nanda-muted" />
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta-contact"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-nanda-terracotta text-white rounded-full px-6 py-3.5 text-sm font-medium"
              >
                {t({ fr: "Contact", en: "Contact", pt: "Contacto" })}
              </Link>
              <div className="pt-5 border-t border-black/5">
                <LanguageSwitcher vertical />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
